/**
 * TableManagementContent - Gestão de Mesas/Comandas.
 * Extraído do App.js (Fase 2 refactor) — mesmo comportamento, apenas reorganização.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Banknote, Bell, CheckCircle, ChevronDown, Copy, CreditCard,
  DollarSign, Eye, EyeOff, Plus, PlusCircle, Search, Settings, ShoppingCart,
  Smartphone, Store, Trash2, TrendingUp, UserCircle, Users, UsersRound, XCircle
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { useAuth } from '../../contexts/AppAuthContext';
import { fuzzyMatch } from '../../utils/helpers';
import { getBackendUrl } from '../../utils/api';
import { TableFilters } from '../../TableManagementImproved';
import WaiterSelector from '../WaiterSelector';
import TableOrderModal from './tables/TableOrderModal';
import TableCloseOrderModal from './tables/TableCloseOrderModal';

const API = `${getBackendUrl()}/api`;

const TableManagementContent = ({ isCloseOrderModalOpen, setIsCloseOrderModalOpen, isOrderModalOpen, setIsOrderModalOpen, setActiveSection }) => {
  const { restaurant } = useAuth();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isTableFormOpen, setIsTableFormOpen] = useState(false);
  const [isSplitBillModalOpen, setIsSplitBillModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isBulkCreateModalOpen, setIsBulkCreateModalOpen] = useState(false);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [isEditTableSelectionModalOpen, setIsEditTableSelectionModalOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tablesToDelete, setTablesToDelete] = useState([]);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [orderHistory, setOrderHistory] = useState([]);
  const [splitBillData, setSplitBillData] = useState(null);
  const [showProductImages, setShowProductImages] = useState(false);
  const [closeOrderForm, setCloseOrderForm] = useState({
    paymentMethod: 'dinheiro',
    splitPeople: 1,
    applyServiceFee: false,
    waiterId: null,
    waiterName: '',
    cashReceived: ''
  });
  const [bulkCreateForm, setBulkCreateForm] = useState({
    prefix: 'Mesa',
    startNumber: 1,
    endNumber: 10,
    capacity: 4
  });
  const [tableForm, setTableForm] = useState({
    name: '',
    number: null,
    capacity: 4,
    status: 'livre'
  });
  
  // 🆕 NOVOS STATES PARA MELHORIAS UX
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchTables();
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchTables = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const response = await axios.get(`${API}/tables`);
      // Sort tables by number in ascending order
      const sortedTables = response.data.sort((a, b) => {
        const numA = parseInt(a.number) || 0;
        const numB = parseInt(b.number) || 0;
        return numA - numB;
      });
      setTables(sortedTables);
    } catch (error) {
      console.error('Error fetching tables:', error);
      if (!silent) alert('Erro ao carregar mesas');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      // Cache-busting header to ensure fresh data
      const response = await axios.get(`${API}/products`, {
        headers: { 'Cache-Control': 'no-cache' }
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchOrderHistory = async () => {
    try {
      const response = await axios.get(`${API}/tables/orders/history`);
      setOrderHistory(response.data);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const handleCreateTable = async () => {
    try {
      await axios.post(`${API}/tables`, tableForm);
      alert('Mesa criada com sucesso!');
      setIsTableFormOpen(false);
      setTableForm({ name: '', number: null, capacity: 4, status: 'livre' });
      fetchTables();
    } catch (error) {
      console.error('Error creating table:', error);
      alert('Erro ao criar mesa');
    }
  };

  const handleUpdateTable = async () => {
    try {
      await axios.put(`${API}/tables/${selectedTable.id}`, tableForm);
      toast.success('✅ Mesa atualizada com sucesso!', {
        description: `${tableForm.name} - Status: ${tableForm.status === 'livre' ? 'Livre' : tableForm.status === 'ocupada' ? 'Ocupada' : 'Reservada'}`,
        duration: 3000,
      });
      setIsTableFormOpen(false);
      setSelectedTable(null);
      setTableForm({ name: '', number: null, capacity: 4, status: 'livre' });
      fetchTables();
    } catch (error) {
      console.error('Error updating table:', error);
      toast.error('❌ Erro ao atualizar mesa', {
        description: error.response?.data?.detail || 'Tente novamente',
        duration: 3000,
      });
    }
  };

  const handleDeleteTable = async (tableId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta mesa?')) return;
    
    try {
      await axios.delete(`${API}/tables/${tableId}`);
      alert('Mesa excluída com sucesso!');
      fetchTables();
    } catch (error) {
      console.error('Error deleting table:', error);
      alert(error.response?.data?.detail || 'Erro ao excluir mesa');
    }
  };

  const handleOpenOrder = async (table) => {
    try {
      const response = await axios.post(`${API}/tables/${table.id}/open-order`, {
        table_id: table.id
      });
      setSelectedTable(table);
      setCurrentOrder(response.data.order);
      setSelectedCategory('all'); // Reset category filter
      setIsOrderModalOpen(true);
      fetchTables();
    } catch (error) {
      console.error('Error opening order:', error);
      alert(error.response?.data?.detail || 'Erro ao abrir comanda');
    }
  };

  const handleViewOrder = async (table) => {
    try {
      const response = await axios.get(`${API}/tables/${table.id}/current-order`);
      if (response.data.order) {
        setSelectedTable(table);
        setCurrentOrder(response.data.order);
        setSelectedCategory('all'); // Reset category filter
        setIsOrderModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching order:', error);
    }
  };

  const handleAddItemToOrder = async (productId, quantity = 1) => {
    const product = products.find(p => p.id === productId);
    if (!product) {
      toast.error('Produto não encontrado');
      return;
    }
    const optimisticItem = {
      id: `optimistic-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      product_id: productId,
      product_name: product.name,
      quantity,
      unit_price: product.price,
      additionals: [],
      subtotal: Number((product.price * quantity).toFixed(2)),
      notes: '',
      added_at: new Date().toISOString(),
      printed: false,
      _optimistic: true
    };
    const prevOrder = currentOrder;
    if (currentOrder) {
      const newItems = [...(currentOrder.items || []), optimisticItem];
      const newSubtotal = newItems.reduce((acc, it) => acc + (Number(it.subtotal) || 0), 0);
      // Taxa de serviço NÃO é aplicada durante a comanda — só no fechamento (toggle padrão desativado)
      setCurrentOrder({ ...currentOrder, items: newItems, subtotal: newSubtotal, service_fee: 0, total: newSubtotal });
    }
    try {
      const response = await axios.post(`${API}/tables/${selectedTable.id}/add-item`, {
        product_id: productId, quantity, additionals: [], notes: ''
      });
      setCurrentOrder(response.data.order);
      fetchTables({ silent: true });
    } catch (error) {
      console.error('Error adding item:', error);
      setCurrentOrder(prevOrder);
      toast.error(error.response?.data?.detail || 'Erro ao adicionar item');
    }
  };

  const handleRemoveItem = async (itemId) => {
    const prevOrder = currentOrder;
    if (currentOrder) {
      const newItems = (currentOrder.items || []).filter(it => it.id !== itemId);
      const newSubtotal = newItems.reduce((acc, it) => acc + (Number(it.subtotal) || 0), 0);
      // Taxa de serviço NÃO é aplicada durante a comanda — só no fechamento
      setCurrentOrder({ ...currentOrder, items: newItems, subtotal: newSubtotal, service_fee: 0, total: newSubtotal });
    }
    try {
      const response = await axios.delete(`${API}/tables/${selectedTable.id}/remove-item/${itemId}`);
      setCurrentOrder(response.data.order);
      fetchTables({ silent: true });
    } catch (error) {
      console.error('Error removing item:', error);
      setCurrentOrder(prevOrder);
      toast.error(error.response?.data?.detail || 'Erro ao remover item');
    }
  };

  const handleCloseOrder = async () => {
    try {
      await axios.post(`${API}/tables/${selectedTable.id}/close-order`, {
        payment_method: closeOrderForm.paymentMethod,
        apply_service_fee: closeOrderForm.applyServiceFee,
        waiter_id: closeOrderForm.waiterId || undefined,
        waiter_name: closeOrderForm.waiterName || undefined,
        split_people: parseInt(closeOrderForm.splitPeople)
      });
      
      const paymentMethodLabel = closeOrderForm.paymentMethod === 'dinheiro' ? 'Dinheiro' : closeOrderForm.paymentMethod === 'cartao' ? 'Cartão' : 'PIX';
      const changeAmount = closeOrderForm.paymentMethod === 'dinheiro' && closeOrderForm.cashReceived 
        ? (parseFloat(closeOrderForm.cashReceived) - ((currentOrder?.total || 0) * (closeOrderForm.applyServiceFee ? 1.1 : 1))).toFixed(2)
        : null;
      
      toast.success('🎉 Comanda fechada com sucesso!', {
        description: changeAmount && parseFloat(changeAmount) > 0
          ? `Mesa ${selectedTable.number} - ${paymentMethodLabel} | Troco: R$ ${changeAmount}`
          : `Mesa ${selectedTable.number} - Pagamento via ${paymentMethodLabel}`,
        duration: 4000,
      });
      
      setIsCloseOrderModalOpen(false);
      setIsOrderModalOpen(false);
      setCurrentOrder(null);
      setSelectedTable(null);
      setCloseOrderForm({
        paymentMethod: 'dinheiro',
        splitPeople: 1,
        applyServiceFee: false,
        waiterId: null,
        waiterName: '',
        cashReceived: ''
      });
      fetchTables();
    } catch (error) {
      console.error('Error closing order:', error);
      toast.error('❌ Erro ao fechar comanda', {
        description: error.response?.data?.detail || 'Tente novamente mais tarde',
        duration: 4000,
      });
    }
  };

  const handleSplitBill = async (numSplits) => {
    try {
      const response = await axios.post(
        `${API}/tables/${selectedTable.id}/split-bill`,
        { num_splits: parseInt(numSplits) }
      );
      setSplitBillData(response.data);
      setIsSplitBillModalOpen(true);
    } catch (error) {
      console.error('Error splitting bill:', error);
      alert('Erro ao dividir conta');
    }
  };

  const openEditTableForm = (table) => {
    setSelectedTable(table);
    setTableForm({
      name: table.name,
      number: table.number,
      capacity: table.capacity,
      status: table.status
    });
    setIsTableFormOpen(true);
  };

  const openNewTableForm = () => {
    setSelectedTable(null);
    setTableForm({ name: '', number: null, capacity: 4, status: 'livre' });
    setIsTableFormOpen(true);
  };

  const handleBulkCreateTables = async () => {
    try {
      const { prefix, startNumber, endNumber, capacity } = bulkCreateForm;
      
      if (startNumber > endNumber) {
        alert('O número inicial deve ser menor ou igual ao número final!');
        return;
      }

      if (endNumber - startNumber > 100) {
        alert('Você pode criar no máximo 100 mesas por vez!');
        return;
      }

      const tablesToCreate = [];
      for (let i = startNumber; i <= endNumber; i++) {
        tablesToCreate.push({
          name: `${prefix} ${i}`,
          number: i,
          capacity: parseInt(capacity),
          status: 'livre'
        });
      }

      // Create tables in batch
      await Promise.all(
        tablesToCreate.map(table => 
          axios.post(`${API}/tables`, table)
        )
      );

      alert(`${tablesToCreate.length} mesas criadas com sucesso!`);
      setIsBulkCreateModalOpen(false);
      setBulkCreateForm({
        prefix: 'Mesa',
        startNumber: 1,
        endNumber: 10,
        capacity: 4
      });
      fetchTables();
    } catch (error) {
      console.error('Error bulk creating tables:', error);
      alert('Erro ao criar mesas em massa');
    }
  };

  const handleBulkDeleteTables = async () => {
    if (tablesToDelete.length === 0) {
      alert('Selecione pelo menos uma mesa para excluir!');
      return;
    }

    const confirmMessage = `Você tem certeza que deseja excluir ${tablesToDelete.length} mesa(s)?\n\nEsta ação não pode ser desfeita!`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      // Delete tables in batch
      await Promise.all(
        tablesToDelete.map(tableId => 
          axios.delete(`${API}/tables/${tableId}`)
        )
      );

      alert(`${tablesToDelete.length} mesa(s) excluída(s) com sucesso!`);
      setIsBulkDeleteModalOpen(false);
      setTablesToDelete([]);
      fetchTables();
    } catch (error) {
      console.error('Error bulk deleting tables:', error);
      alert('Erro ao excluir mesas. Algumas mesas podem ter comandas ativas.');
    }
  };

  const toggleTableSelection = (tableId) => {
    setTablesToDelete(prev => {
      if (prev.includes(tableId)) {
        return prev.filter(id => id !== tableId);
      } else {
        return [...prev, tableId];
      }
    });
  };

  const selectAllTables = () => {
    if (tablesToDelete.length === tables.length) {
      setTablesToDelete([]);
    } else {
      setTablesToDelete(tables.map(t => t.id));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'livre':
        return 'bg-gradient-to-br from-green-500 to-emerald-600';
      case 'ocupada':
        return 'bg-gradient-to-br from-red-500 to-rose-600';
      case 'reservada':
        return 'bg-gradient-to-br from-yellow-500 to-amber-600';
      default:
        return 'bg-gradient-to-br from-gray-500 to-slate-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'livre':
        return '✓';
      case 'ocupada':
        return '🍽️';
      case 'reservada':
        return '🔒';
      default:
        return '○';
    }
  };

  // 🆕 Filtrar e buscar mesas
  const filteredTables = tables.filter(table => {
    // Filtro por status
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'livre' && !table.has_order && table.status === 'livre') ||
      (filterStatus === 'ocupada' && (table.has_order || table.status === 'ocupada')) ||
      (filterStatus === 'reservada' && table.status === 'reservada');
    
    // Busca por nome ou número
    const matchesSearch = !searchQuery || 
      table.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (table.number && table.number.toString().includes(searchQuery));
    
    return matchesStatus && matchesSearch;
  });

  const handleClearFilters = () => {
    setSearchQuery('');
    setFilterStatus('all');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f0f10]" data-testid="table-management-content">
      {/* 🆕 Header Clean e Compacto */}
      <div className="bg-[#0f0f10] px-4 py-3 md:px-6 md:py-4 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <h1 className="text-xl md:text-2xl font-bold text-white">Mesas</h1>
          
          {/* Botões Discretos lado a lado */}
          <div className="flex items-center gap-2">
            {/* Botão Visão Geral - Discreto */}
            <button
              onClick={() => {
                const dashboard = document.querySelector('[data-testid="dashboard-toggle"]');
                if (dashboard) dashboard.click();
              }}
              className="p-2 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg transition-colors"
              title="Visão Geral"
            >
              <TrendingUp className="w-4 h-4 text-gray-400" />
            </button>

            {/* Dropdown Nova Mesa - Discreto */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="p-2 bg-gray-800/50 hover:bg-gray-700/50 text-gray-400 hover:text-white rounded-lg transition-colors flex items-center gap-1"
                  data-testid="new-table-dropdown"
                  title="Gerenciar Mesas"
                >
                  <Settings className="w-4 h-4" />
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-gray-800 border-gray-700">
                <DropdownMenuItem 
                  onClick={openNewTableForm}
                  className="cursor-pointer text-white hover:bg-gray-700 focus:bg-gray-700"
                  data-testid="single-table-option"
                >
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Criar Mesa Individual
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => setIsBulkCreateModalOpen(true)}
                  className="cursor-pointer text-white hover:bg-gray-700 focus:bg-gray-700"
                  data-testid="bulk-table-option"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Criar Mesas em Massa
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setIsEditTableSelectionModalOpen(true)}
                  className="cursor-pointer text-white hover:bg-gray-700 focus:bg-gray-700"
                  data-testid="edit-table-option"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Editar Mesa
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => {
                    setTablesToDelete([]);
                    setIsBulkDeleteModalOpen(true);
                  }}
                  className="cursor-pointer text-red-400 hover:bg-red-500/10 focus:bg-red-500/10"
                  data-testid="delete-tables-option"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Excluir Mesas
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* 🆕 Busca e Filtros */}
      <div className="px-4 md:px-6 py-3">
        <TableFilters
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          onClear={handleClearFilters}
        />
      </div>

      {/* Tables Grid - Clean e Funcional */}
      <div className="px-4 pb-6 md:px-6">
        {/* 🆕 Results Counter */}
        {(searchQuery || filterStatus !== 'all') && filteredTables.length > 0 && (
          <div className="mb-4 flex items-center justify-between bg-gray-800/30 rounded-lg px-4 py-2 border border-gray-700/50">
            <span className="text-gray-400 text-sm">
              {filteredTables.length === tables.length 
                ? `${filteredTables.length} ${filteredTables.length === 1 ? 'mesa encontrada' : 'mesas encontradas'}`
                : `${filteredTables.length} de ${tables.length} ${filteredTables.length === 1 ? 'mesa' : 'mesas'}`
              }
            </span>
            <button
              onClick={handleClearFilters}
              className="text-orange-400 hover:text-orange-300 text-sm font-medium transition-colors flex items-center gap-1"
            >
              <XCircle className="w-4 h-4" />
              Limpar
            </button>
          </div>
        )}

        {/* 🆕 GRID CLEAN E FUNCIONAL - 3 colunas, cards quadrados */}
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
          {filteredTables.map((table) => {
            const isOccupied = table.status === 'ocupada' || table.has_order;
            
            return (
              <div
                key={table.id}
                className={`relative group bg-slate-800/30 backdrop-blur-sm rounded-xl p-4 cursor-pointer transition-all duration-200 hover:scale-[1.03] aspect-square flex flex-col items-center justify-between ${
                  isOccupied 
                    ? 'border-[3px] border-orange-500 shadow-md shadow-orange-500/20 hover:shadow-lg hover:shadow-orange-500/30' 
                    : 'border-[3px] border-green-500 shadow-md shadow-green-500/20 hover:shadow-lg hover:shadow-green-500/30'
                }`}
                onClick={() => {
                  if (table.has_order) {
                    handleViewOrder(table);
                  } else {
                    handleOpenOrder(table);
                  }
                }}
                data-testid={`table-card-${table.id}`}
              >
                {/* Número Grande */}
                <div className="flex-1 flex items-center justify-center w-full">
                  <div className="text-white text-4xl sm:text-5xl md:text-6xl font-black leading-none tracking-tight">
                    {table.number || table.name.match(/\d+/)?.[0] || table.name.charAt(0)}
                  </div>
                </div>

                {/* Capacidade - Compacta */}
                <div className="flex items-center gap-1 text-gray-400 text-xs mb-2">
                  <Users className="w-3 h-3" />
                  <span>{table.capacity}</span>
                </div>

                {/* Total (apenas se ocupada) */}
                {isOccupied && table.current_order && (
                  <div className="text-orange-400 font-bold text-sm mb-2">
                    R$ {table.current_order.total?.toFixed(2) || '0.00'}
                  </div>
                )}

                {/* Badge Status - Compacto */}
                <div className={`w-full py-1.5 px-2 rounded-lg text-xs font-bold text-center ${
                  isOccupied
                    ? 'bg-orange-500/80 text-white'
                    : 'bg-green-500/80 text-white'
                }`}>
                  {isOccupied ? 'Ocupada' : 'Livre'}
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State - No tables at all */}
        {tables.length === 0 && (
          <div className="text-center py-20">
            <div className="bg-slate-800/30 rounded-full w-24 h-24 mx-auto mb-4 flex items-center justify-center">
              <Store className="w-12 h-12 text-gray-500" />
            </div>
            <h3 className="text-white font-bold text-xl mb-2">Nenhuma mesa cadastrada</h3>
            <p className="text-gray-400 mb-6">Comece criando suas primeiras mesas</p>
            <button
              onClick={openNewTableForm}
              className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl transition-all font-semibold shadow-lg shadow-orange-500/30"
            >
              Criar Primeira Mesa
            </button>
          </div>
        )}

        {/* 🆕 Empty State - No results from filter/search */}
        {tables.length > 0 && filteredTables.length === 0 && (
          <div className="text-center py-20">
            <div className="bg-slate-800/30 rounded-full w-24 h-24 mx-auto mb-4 flex items-center justify-center">
              <Search className="w-12 h-12 text-gray-500" />
            </div>
            <h3 className="text-white font-bold text-xl mb-2">Nenhuma mesa encontrada</h3>
            <p className="text-gray-400 mb-6">
              {searchQuery 
                ? `Não encontramos mesas com "${searchQuery}"`
                : `Não há mesas com status "${filterStatus}"`
              }
            </p>
            <button
              onClick={handleClearFilters}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl transition-all font-semibold shadow-lg shadow-blue-500/30"
            >
              Limpar Filtros
            </button>
          </div>
        )}
      </div>

      {/* Bulk Create Tables Modal */}
      {isBulkCreateModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="bulk-create-modal">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl max-w-lg w-full p-6 border border-gray-700">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-white">Criar Mesas em Massa</h2>
                <p className="text-gray-400 text-sm mt-1">Crie várias mesas de uma só vez</p>
              </div>
              <button
                onClick={() => setIsBulkCreateModalOpen(false)}
                className="w-10 h-10 bg-gray-700 hover:bg-gray-600 rounded-lg flex items-center justify-center transition-colors"
                data-testid="close-bulk-modal-button"
              >
                <XCircle className="w-5 h-5 text-gray-300" />
              </button>
            </div>

            {/* Preview Card */}
            <div className="bg-gradient-to-r from-orange-500/20 to-amber-500/20 border border-orange-500/30 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Copy className="w-4 h-4 text-orange-400" />
                <p className="text-orange-300 text-sm font-medium">Pré-visualização</p>
              </div>
              <p className="text-white font-bold">
                {bulkCreateForm.endNumber - bulkCreateForm.startNumber + 1} mesas serão criadas
              </p>
              <p className="text-gray-300 text-sm mt-1">
                De &quot;{bulkCreateForm.prefix} {bulkCreateForm.startNumber}&quot; até &quot;{bulkCreateForm.prefix} {bulkCreateForm.endNumber}&quot;
              </p>
            </div>

            <div className="space-y-4">
              {/* Prefix */}
              <div>
                <label className="text-white font-medium text-sm mb-2 block">
                  Prefixo do Nome
                </label>
                <input
                  type="text"
                  value={bulkCreateForm.prefix}
                  onChange={(e) => setBulkCreateForm({ ...bulkCreateForm, prefix: e.target.value })}
                  placeholder="Ex: Mesa, Salão"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                  data-testid="bulk-prefix-input"
                />
                <p className="text-gray-500 text-xs mt-1">Será usado como: {bulkCreateForm.prefix} 1, {bulkCreateForm.prefix} 2...</p>
              </div>

              {/* Number Range */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-white font-medium text-sm mb-2 block">
                    Número Inicial
                  </label>
                  <input
                    type="number"
                    value={bulkCreateForm.startNumber}
                    onChange={(e) => setBulkCreateForm({ ...bulkCreateForm, startNumber: parseInt(e.target.value) || 1 })}
                    min="1"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-orange-500 transition-colors"
                    data-testid="bulk-start-number-input"
                  />
                </div>
                <div>
                  <label className="text-white font-medium text-sm mb-2 block">
                    Número Final
                  </label>
                  <input
                    type="number"
                    value={bulkCreateForm.endNumber}
                    onChange={(e) => setBulkCreateForm({ ...bulkCreateForm, endNumber: parseInt(e.target.value) || 1 })}
                    min={bulkCreateForm.startNumber}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-orange-500 transition-colors"
                    data-testid="bulk-end-number-input"
                  />
                </div>
              </div>

              {/* Capacity */}
              <div>
                <label className="text-white font-medium text-sm mb-2 block">
                  Capacidade (pessoas)
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setBulkCreateForm({ ...bulkCreateForm, capacity: Math.max(1, bulkCreateForm.capacity - 1) })}
                    className="w-10 h-10 bg-gray-700 hover:bg-gray-600 rounded-lg flex items-center justify-center transition-colors text-white font-bold text-lg"
                    data-testid="bulk-capacity-decrease"
                  >
                    −
                  </button>
                  <div className="flex-1 bg-gray-800 border border-gray-700 rounded-lg p-3 text-center">
                    <p className="text-white text-2xl font-bold">{bulkCreateForm.capacity}</p>
                    <p className="text-gray-400 text-xs">pessoas por mesa</p>
                  </div>
                  <button
                    onClick={() => setBulkCreateForm({ ...bulkCreateForm, capacity: bulkCreateForm.capacity + 1 })}
                    className="w-10 h-10 bg-gray-700 hover:bg-gray-600 rounded-lg flex items-center justify-center transition-colors text-white font-bold text-lg"
                    data-testid="bulk-capacity-increase"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            {/* Warning */}
            {(bulkCreateForm.endNumber - bulkCreateForm.startNumber + 1) > 50 && (
              <div className="mt-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 flex items-start gap-2">
                <Bell className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-yellow-300 text-sm font-medium">Atenção!</p>
                  <p className="text-yellow-200 text-xs mt-0.5">Você está criando muitas mesas. Isso pode demorar alguns segundos.</p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setIsBulkCreateModalOpen(false)}
                className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium"
                data-testid="cancel-bulk-create-button"
              >
                Cancelar
              </button>
              <button
                onClick={handleBulkCreateTables}
                disabled={!bulkCreateForm.prefix || bulkCreateForm.startNumber > bulkCreateForm.endNumber}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white rounded-lg transition-all font-bold shadow-lg shadow-orange-500/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="confirm-bulk-create-button"
              >
                <CheckCircle className="w-5 h-5" />
                Criar Mesas
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Tables Modal */}
      {isBulkDeleteModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="bulk-delete-modal">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden border border-gray-700 flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-600 to-rose-600 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white">Excluir Mesas</h2>
                <p className="text-red-100 text-sm mt-1">Selecione as mesas que deseja remover</p>
              </div>
              <button
                onClick={() => {
                  setIsBulkDeleteModalOpen(false);
                  setTablesToDelete([]);
                }}
                className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors"
                data-testid="close-bulk-delete-modal-button"
              >
                <XCircle className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Selection Info */}
            <div className="px-6 py-4 bg-gray-800/50 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-red-500/20 border border-red-500/30 rounded-lg px-3 py-2">
                    <p className="text-red-300 text-sm font-medium">
                      {tablesToDelete.length} de {tables.length} selecionada(s)
                    </p>
                  </div>
                  <button
                    onClick={selectAllTables}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors font-medium"
                    data-testid="select-all-tables-button"
                  >
                    {tablesToDelete.length === tables.length ? 'Desmarcar Todas' : 'Selecionar Todas'}
                  </button>
                </div>
              </div>
            </div>

            {/* Tables List */}
            <div className="flex-1 overflow-y-auto p-6">
              {tables.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-400">Nenhuma mesa cadastrada</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {tables.map((table) => {
                    const isSelected = tablesToDelete.includes(table.id);
                    const hasActiveOrder = table.status === 'ocupada' || table.has_order;
                    
                    return (
                      <div
                        key={table.id}
                        onClick={() => !hasActiveOrder && toggleTableSelection(table.id)}
                        className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                          hasActiveOrder
                            ? 'bg-gray-800/50 border-gray-700 opacity-50 cursor-not-allowed'
                            : isSelected
                            ? 'bg-red-500/20 border-red-500 shadow-lg shadow-red-500/20'
                            : 'bg-gray-800 border-gray-700 hover:border-gray-600'
                        }`}
                        data-testid={`delete-table-item-${table.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${
                              isSelected
                                ? 'bg-red-500 border-red-500'
                                : 'border-gray-600'
                            }`}>
                              {isSelected && (
                                <CheckCircle className="w-4 h-4 text-white" />
                              )}
                            </div>
                            <div>
                              <p className={`font-bold ${isSelected ? 'text-white' : 'text-gray-300'}`}>
                                {table.name}
                              </p>
                              <p className="text-gray-500 text-xs">
                                {table.capacity} pessoas
                              </p>
                            </div>
                          </div>
                          {hasActiveOrder && (
                            <div className="bg-orange-500/20 border border-orange-500/30 rounded px-2 py-1">
                              <p className="text-orange-400 text-xs font-medium">Em uso</p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer with Actions */}
            <div className="px-6 py-4 bg-gray-800/50 border-t border-gray-700">
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setIsBulkDeleteModalOpen(false);
                    setTablesToDelete([]);
                  }}
                  className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium"
                  data-testid="cancel-bulk-delete-button"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleBulkDeleteTables}
                  disabled={tablesToDelete.length === 0}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white rounded-lg transition-all font-bold shadow-lg shadow-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="confirm-bulk-delete-button"
                >
                  <Trash2 className="w-5 h-5" />
                  Excluir {tablesToDelete.length > 0 ? `(${tablesToDelete.length})` : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Table Selection Modal - abre a partir do dropdown "Editar Mesa" */}
      {isEditTableSelectionModalOpen && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          data-testid="edit-table-selection-modal"
        >
          <div className="bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-xl px-5 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-white font-bold text-lg">Editar Mesa</h2>
                <p className="text-blue-100 text-xs mt-0.5">Selecione a mesa que deseja editar</p>
              </div>
              <button
                type="button"
                onClick={() => setIsEditTableSelectionModalOpen(false)}
                className="w-8 h-8 rounded-md bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                data-testid="close-edit-table-selection-modal"
                aria-label="Fechar"
              >
                ×
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {tables.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <Store className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>Nenhuma mesa cadastrada.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {tables.map((table) => {
                    const initial = String(table.number || table.name || '?').charAt(0).toUpperCase();
                    return (
                      <button
                        key={table.id}
                        type="button"
                        onClick={() => {
                          setIsEditTableSelectionModalOpen(false);
                          openEditTableForm(table);
                        }}
                        data-testid={`edit-table-select-${table.id}`}
                        className="w-full flex items-center gap-3 p-3 bg-slate-700/40 hover:bg-slate-600/60 border border-slate-600 rounded-lg transition-colors text-left"
                      >
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white font-bold flex items-center justify-center shrink-0">
                          {initial}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{table.name || `Mesa ${table.number}`}</p>
                          <p className="text-xs text-gray-400">{(table.capacity ?? 0)} pessoas</p>
                        </div>
                        <Settings className="w-4 h-4 text-gray-400 shrink-0" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="border-t border-slate-700 p-4 flex justify-end">
              <button
                type="button"
                onClick={() => setIsEditTableSelectionModalOpen(false)}
                data-testid="close-edit-table-selection-footer"
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table Form Modal */}
      {isTableFormOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="table-form-modal">
          <div className="bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-white mb-6">
              {selectedTable ? 'Editar Mesa' : 'Nova Mesa'}
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Nome da Mesa *
                </label>
                <input
                  type="text"
                  value={tableForm.name}
                  onChange={(e) => setTableForm({ ...tableForm, name: e.target.value })}
                  placeholder="Ex: Mesa 1, Mesa VIP"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="table-name-input"
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Número (opcional)
                </label>
                <input
                  type="number"
                  value={tableForm.number || ''}
                  onChange={(e) => setTableForm({ ...tableForm, number: e.target.value ? parseInt(e.target.value) : null })}
                  placeholder="Ex: 1, 2, 3..."
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="table-number-input"
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Capacidade (pessoas) *
                </label>
                <input
                  type="number"
                  value={tableForm.capacity}
                  onChange={(e) => setTableForm({ ...tableForm, capacity: parseInt(e.target.value) || 1 })}
                  min="1"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="table-capacity-input"
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Status
                </label>
                <select
                  value={tableForm.status}
                  onChange={(e) => setTableForm({ ...tableForm, status: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="table-status-select"
                >
                  <option value="livre">Livre</option>
                  <option value="ocupada">Ocupada</option>
                  <option value="reservada">Reservada</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setIsTableFormOpen(false);
                  setSelectedTable(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                data-testid="cancel-table-form-button"
              >
                Cancelar
              </button>
              <button
                onClick={selectedTable ? handleUpdateTable : handleCreateTable}
                disabled={!tableForm.name || !tableForm.capacity}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-table-form-button"
              >
                {selectedTable ? 'Atualizar' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Management Modal - Modern Digital Menu Style */}
      {isOrderModalOpen && currentOrder && selectedTable && (
        <TableOrderModal categories={categories} currentOrder={currentOrder} handleAddItemToOrder={handleAddItemToOrder} handleRemoveItem={handleRemoveItem} productSearchQuery={productSearchQuery} products={products} selectedCategory={selectedCategory} selectedTable={selectedTable} setCurrentOrder={setCurrentOrder} setIsCloseOrderModalOpen={setIsCloseOrderModalOpen} setIsOrderModalOpen={setIsOrderModalOpen} setProductSearchQuery={setProductSearchQuery} setSelectedCategory={setSelectedCategory} setSelectedTable={setSelectedTable} setShowProductImages={setShowProductImages} showProductImages={showProductImages} tables={tables} />
      )}

      {/* Close Order Screen - Fullscreen */}
      {isCloseOrderModalOpen && currentOrder && (
        <TableCloseOrderModal closeOrderForm={closeOrderForm} currentOrder={currentOrder} handleCloseOrder={handleCloseOrder} restaurant={restaurant} selectedTable={selectedTable} setActiveSection={setActiveSection} setCloseOrderForm={setCloseOrderForm} setIsCloseOrderModalOpen={setIsCloseOrderModalOpen} />
      )}

      {/* Split Bill Modal */}
      {isSplitBillModalOpen && splitBillData && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4" data-testid="split-bill-modal">
          <div className="bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-white mb-4">Conta Dividida</h2>
            <div className="space-y-3 mb-6">
              <div className="bg-gray-700 rounded-lg p-3">
                <p className="text-gray-400 text-sm">Total da Conta</p>
                <p className="text-white text-2xl font-bold">R$ {splitBillData.total.toFixed(2)}</p>
              </div>
              
              {splitBillData.splits.map((split) => (
                <div key={split.person} className="bg-gray-700 rounded-lg p-3 flex justify-between items-center">
                  <span className="text-white font-medium">Pessoa {split.person}</span>
                  <span className="text-indigo-400 text-lg font-bold">R$ {split.amount.toFixed(2)}</span>
                </div>
              ))}
            </div>
            
            <button
              onClick={() => {
                setIsSplitBillModalOpen(false);
                setSplitBillData(null);
              }}
              className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              data-testid="close-split-bill-modal-button"
            >
              Fechar
            </button>
          </div>
        </div>
      )}

      {/* History Modal */}
      {isHistoryModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="history-modal">
          <div className="bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full my-8">
            <div className="bg-gradient-to-r from-indigo-500 to-violet-500 px-6 py-4 rounded-t-xl">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">Histórico de Comandas</h2>
                <button
                  onClick={() => setIsHistoryModalOpen(false)}
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors"
                  data-testid="close-history-modal-button"
                >
                  <XCircle className="w-6 h-6 text-white" />
                </button>
              </div>
            </div>

            <div className="p-6 max-h-[600px] overflow-y-auto">
              {orderHistory.length > 0 ? (
                <div className="space-y-3">
                  {orderHistory.map((order) => (
                    <div key={order.id} className="bg-gray-700 rounded-lg p-4" data-testid={`history-order-${order.id}`}>
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="text-white font-bold">{order.table_name}</h3>
                          <p className="text-gray-400 text-sm">
                            {new Date(order.closed_at).toLocaleDateString('pt-BR')} às{' '}
                            {new Date(order.closed_at).toLocaleTimeString('pt-BR')}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-green-400 font-bold text-lg">R$ {order.total?.toFixed(2)}</p>
                          <p className="text-gray-400 text-sm capitalize">{order.payment_method}</p>
                        </div>
                      </div>
                      <p className="text-gray-400 text-sm">
                        {order.items?.length || 0} {order.items?.length === 1 ? 'item' : 'itens'}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-8">Nenhuma comanda fechada encontrada</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default TableManagementContent;
